import type { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { Button, Caption, Chip, InlineButtons, List, Placeholder, Section, Title } from '@telegram-apps/telegram-ui';
import { formatImageUrl } from '../../lib/imageUrl';
import { PRODUCT_IMAGE_ASPECT_RATIO, PRODUCT_IMAGE_BACKGROUND, PRODUCT_IMAGE_OBJECT_FIT } from '../../lib/imagePolicy';
import type { Product, ProductMediaItem } from '../../types/product';
import { STATUS_LABELS, formatRelativeRu, previewUrlFromProduct } from './productModalUtils';

type ProductViewPanelProps = {
  product: Product;
  media: ProductMediaItem[];
  selectedMedia: ProductMediaItem | null;
  selectedMediaIndex: number;
  swipeHandlers: ReturnType<typeof useSwipeNavigation>;
  onSelectMediaIndex: (index: number) => void;
  onOpenViewer: (item: ProductMediaItem) => void;
  onOpenLinks: () => void;
  onOpenShoot: () => void;
  onOpenGallery: () => void;
  onOpenEdit: () => void;
  onShare: () => void;
  onClose: () => void;
};

export function ProductViewPanel({
  product,
  media,
  selectedMedia,
  selectedMediaIndex,
  swipeHandlers,
  onSelectMediaIndex,
  onOpenViewer,
  onOpenLinks,
  onOpenShoot,
  onOpenGallery,
  onOpenEdit,
  onShare,
  onClose,
}: ProductViewPanelProps) {
  const imgSrc = selectedMedia
    ? formatImageUrl(selectedMedia.thumbnailUrl || selectedMedia.url)
    : previewUrlFromProduct(product);

  return (
    <>
      <List style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <div style={{ padding: '0', textAlign: 'center', backgroundColor: '#f0f0f0' }}>
          {imgSrc ? (
            <button
              type="button"
              {...swipeHandlers}
              onClick={() => selectedMedia && onOpenViewer(selectedMedia)}
              style={{
                width: '100%',
                padding: 0,
                border: 0,
                background: 'transparent',
                cursor: selectedMedia ? 'zoom-in' : 'default',
                touchAction: media.length > 1 ? 'pan-y' : 'auto',
              }}
            >
              <img
                src={imgSrc}
                alt={selectedMedia?.title ?? product.name}
                style={{
                  width: '100%',
                  aspectRatio: PRODUCT_IMAGE_ASPECT_RATIO,
                  maxHeight: '400px',
                  objectFit: PRODUCT_IMAGE_OBJECT_FIT,
                  display: 'block',
                  background: PRODUCT_IMAGE_BACKGROUND,
                }}
              />
            </button>
          ) : (
            <Placeholder header="Изображение" description="URL недоступен для показа" />
          )}
        </div>

        {selectedMedia && (
          <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <Caption>{selectedMedia.title}</Caption>
            <Caption>{selectedMediaIndex + 1}/{media.length}</Caption>
          </div>
        )}

        {media.length > 1 && (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 16px 12px' }}>
            {media.map((item, idx) => {
              const thumb = formatImageUrl(item.thumbnailUrl || item.url);
              const activeThumb = idx === selectedMediaIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelectMediaIndex(idx)}
                  aria-label={item.title}
                  style={{
                    width: 64,
                    height: 80,
                    flex: '0 0 auto',
                    padding: 0,
                    border: activeThumb
                      ? '2px solid var(--tg-theme-button-color, #2481cc)'
                      : '1px solid var(--tgui--separator_color)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: PRODUCT_IMAGE_BACKGROUND,
                  }}
                >
                  <img
                    src={thumb}
                    alt={item.title}
                    style={{ width: '100%', height: '100%', objectFit: PRODUCT_IMAGE_OBJECT_FIT, display: 'block' }}
                  />
                </button>
              );
            })}
          </div>
        )}

        <Section header="Название">
          <Title level="2" style={{ paddingLeft: 20 }}>{product.name}</Title>
        </Section>
        <Section header="Поля">
          <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <Caption style={{ opacity: 0.6 }}>SKU</Caption>
              <Title level="3">{product.sku ?? '—'}</Title>
            </div>
            <div>
              <Caption style={{ opacity: 0.6 }}>Категория</Caption>
              <Title level="3">{product.category ?? '—'}</Title>
            </div>
          </div>
        </Section>
        <Section header="Статус">
          <div style={{ padding: '0 20px' }}>
            <Caption style={{ fontSize: '16px' }}>{STATUS_LABELS[product.status]}</Caption>
          </div>
        </Section>
        <Section header="Теги">
          {product.tags.length === 0 ? (
            <div style={{ padding: '0 20px' }}>
              <Caption>нет тегов</Caption>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 20px 12px 20px' }}>
              {product.tags.map((tag) => (
                <Chip key={tag} mode="outline" style={{ pointerEvents: 'none' }}>
                  {tag}
                </Chip>
              ))}
            </div>
          )}
        </Section>
        <Section header="Даты">
          <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Caption>Создан: {formatRelativeRu(product.createdAt)}</Caption>
            <Caption>Обновлён: {formatRelativeRu(product.updatedAt)}</Caption>
          </div>
        </Section>
        {product.status === 'active' && (
          <Section header="Клиент">
            <div style={{ padding: '0 20px 12px' }}>
              <Button mode="filled" stretched onClick={onOpenLinks}>
                Отправить клиенту
              </Button>
            </div>
          </Section>
        )}
      </List>

      {product.status !== 'archived' && !!product.sourceImageUrl ? (
        <InlineButtons mode="gray" style={{ padding: '8px 16px 12px' }}>
          <InlineButtons.Item text="📸 Сгенерировать фото" onClick={onOpenShoot} />
          <InlineButtons.Item text="Галерея" onClick={onOpenGallery} />
          <InlineButtons.Item text="Редактировать" onClick={onOpenEdit} />
          <InlineButtons.Item text="Поделиться" disabled={!selectedMedia} onClick={onShare} />
        </InlineButtons>
      ) : (
        <InlineButtons mode="gray" style={{ padding: '8px 16px 12px' }}>
          <InlineButtons.Item text="Галерея" onClick={onOpenGallery} />
          <InlineButtons.Item text="Редактировать" onClick={onOpenEdit} />
          <InlineButtons.Item text="Поделиться" disabled={!selectedMedia} onClick={onShare} />
          <InlineButtons.Item text="Закрыть" onClick={onClose} />
        </InlineButtons>
      )}
    </>
  );
}
